import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Assets as AssetsIcon } from '@/components/chariot-ui';
import React, { useMemo } from 'react';

import { Loader } from '@/components/Loader';
import { Timeline } from '@/components/Timeline';
import { getNotes } from '@/sections/Notes';
import { Asset, Port, Technology } from '@/types';
import { QueryStatus } from '@/utils/api';
import { formatDate } from '@/utils/date.util';
import { getTechnologyDisplayProduct } from '@/utils/entities/technology.util';

interface ImpactedAssetPort {
  asset: Asset;
  port: Port;
  visited: string;
}

export interface TechnologyHistoryProps {
  technology: Technology;
  impactedAssetPorts: ImpactedAssetPort[];
  impactedAssetsTreeStatus: QueryStatus;
}

export const TechnologyHistory: React.FC<TechnologyHistoryProps> = ({
  technology,
  impactedAssetPorts,
  impactedAssetsTreeStatus,
}) => {
  const notes = useMemo(() => getNotes(technology), [technology]);

  const historyItems = useMemo(() => {
    const notesCreated = notes.map(note => ({
      ...note,
      created: note.date,
    }));
    const assetsPortsCreated = impactedAssetPorts.map(assetPort => ({
      ...assetPort,
      created: assetPort.asset.created,
    }));
    const assetsNotesSorted = [...assetsPortsCreated, ...notesCreated].sort(
      (a, b) => {
        return new Date(b.created).getTime() - new Date(a.created).getTime();
      }
    );

    return [
      ...assetsNotesSorted.map(assetNote => {
        const isAsset = 'asset' in assetNote;
        if (isAsset) {
          return {
            title: (
              <p>
                {assetNote.asset.group} - {assetNote.asset.identifier} (
                {assetNote.port.protocol} {assetNote.port.port}){` added to `}
                <span className="font-bold">Assets Impacted</span>
              </p>
            ),
            description: assetNote.created && formatDate(assetNote.created),
            icon: <AssetsIcon />,
          };
        }

        return {
          title: (
            <p className="inline">
              <span>
                {assetNote.author} added the
                <span className="font-semibold"> Note </span>
                <i style={{ wordBreak: 'break-word' }}>{assetNote.message}</i>
              </span>
            </p>
          ),
          description: assetNote?.created && formatDate(assetNote?.created),
        };
      }),
      {
        title: (
          <p>
            <span className="font-bold">First Tracked</span> on{' '}
            {getTechnologyDisplayProduct(technology)}
          </p>
        ),
        description: technology.created && formatDate(technology.created),
        icon: <ExclamationTriangleIcon />,
      },
    ];
  }, [technology, impactedAssetPorts, notes]);

  return (
    <Loader
      isLoading={impactedAssetsTreeStatus === 'pending'}
      className="m-6 w-[calc(100%-48px)]"
    >
      <Timeline items={historyItems} />
    </Loader>
  );
};
